import { merge } from 'lodash'

import type { _Map, MapDatasetGroup, Site, Dataset, ListDatasetsResponse } from 'api/internal/generated'
import { getMapOptions, getSiteOptions, listDatasetsOptions } from 'api/internal/generated/@tanstack/react-query.gen'
import { queryClient } from 'api/internal/queryClient'
import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import type { ConfigLayerFolderOptions } from 'config/ConfigLayerFolderOptions'
import { createConfigLayerFolder } from 'config/ConfigLayerFolderOptions'
import type { ConfigLayerType } from 'config/ConfigLayerType'

const API_DATASET_TYPES_ENUMS = {
  QGISProject: 1,
  WFS: 2,
  WMS: 3,
  XYZ: 4,
  MVT: 5
} as const

const DATASET_TYPE_MAPPING: { [key: number]: string } = {
  [API_DATASET_TYPES_ENUMS.QGISProject]: 'QGISProjectSettings',
  [API_DATASET_TYPES_ENUMS.WFS]: 'GeoJSON',
  [API_DATASET_TYPES_ENUMS.WMS]: 'TileWMS',
  [API_DATASET_TYPES_ENUMS.XYZ]: 'XYZ',
  [API_DATASET_TYPES_ENUMS.MVT]: 'Maplibre-GL'
}

// Type guard to check if an item is a Dataset
const isDataset = (item: Dataset | MapDatasetGroup): item is Dataset => {
  return 'id' in item && 'type' in item && 'url' in item
}

// Type guard to check if an item is a MapDatasetGroup
const isMapDatasetGroup = (item: Dataset | MapDatasetGroup): item is MapDatasetGroup => {
  return 'group_name' in item && 'datasets' in item
}

const getSiteConfigUrl = async (site: string) => {
  const getSiteQueryOptions = getSiteOptions({
    headers: {
      'X-Pozi-Sitename': site
    }
  })
  return await queryClient.fetchQuery(getSiteQueryOptions)
}

const getMapConfigUrl = async (site: string, map: string) => {
  const getMapQueryOptions = getMapOptions({
    headers: {
      'X-Pozi-Sitename': site
    },
    path: {
      map_name: map
    }
  })
  return await queryClient.fetchQuery(getMapQueryOptions)
}

const getDatasetsConfigUrl = async (site: string, map: string) => {
  const getDatasetsQueryOptions = listDatasetsOptions({
    headers: {
      'X-Pozi-Sitename': site
    },
    path: {
      map_name: map
    }
  })
  return await queryClient.fetchQuery(getDatasetsQueryOptions)
}

const fetchAllConfig = async (site: string, map: string) => {
  const siteConfig = await getSiteConfigUrl(site)
  const mapConfig = await getMapConfigUrl(site, map)
  const datasetsConfig = await getDatasetsConfigUrl(site, map)

  const config = {
    site: siteConfig,
    map: mapConfig,
    datasets: datasetsConfig
  }

  console.debug('fetchAllConfig', site, map, config)

  return config
}

const transformSiteConfig = (siteConfig: Site) => {
  return {
    layout: {
      ...(siteConfig?.favicon_url && { favicon: siteConfig?.favicon_url }),
      ...(siteConfig?.site_logo_url && { mapicon: siteConfig?.site_logo_url }),
      ...(siteConfig?.print_logo_url && { printicon: siteConfig?.print_logo_url })
    }
  }
}

const transformMapConfig = (mapConfig: _Map) => {
  return {
    title: mapConfig.title,
    layout: {
      drawer: {
        tabs: {
          about: {
            ...(mapConfig?.description && { html: mapConfig.description })
          },
          tools: {
            print: {
              stats: {
                ...(mapConfig?.disclaimer && { disclaimer: mapConfig.disclaimer })
              }
            }
          }
        }
      },
      map: {
        views: {
          bbox: [
            [mapConfig.bounding_box[0], mapConfig.bounding_box[1]],
            [mapConfig.bounding_box[2], mapConfig.bounding_box[3]]
          ]
        }
      }
    }
  }
}

// Recursive function to process datasets and nested groups
const processDatasetsRecursively = (datasetGroup: MapDatasetGroup, parentLayer: ConfigLayerFolderOptions): ConfigLayerOptions[] => {
  const mapLayers: ConfigLayerOptions[] = []

  for (const dataset of datasetGroup?.datasets || []) {
    const datasetTitle = isDataset(dataset) ? dataset.title : dataset.group_name
    const depth = parentLayer?.depth ? parentLayer.depth + 1 : 1
    const datasetGroup = depth > 1 ? '' : parentLayer?.title || ''
    const showLegend = isDataset(dataset) ? dataset.show_legend : true

    // Depending on whether the dataset is a dataset or a group, create a different type of layer
    const mapLayer: ConfigLayerOptions = isDataset(dataset)
      ? {
          title: datasetTitle,
          titleid: PoziUtils.getPoziIDFromString(datasetTitle),
          group: datasetGroup,
          groupid: PoziUtils.getPoziIDFromString(datasetGroup),
          type: DATASET_TYPE_MAPPING[dataset.type] as ConfigLayerType,
          parentid: parentLayer?.titleid,

          // Sets show legend attribute to undefined when dataset is of QGIS type
          // This is to avoid global setting for the whole QGIS project
          // QGIS project set legend visibility at layer level using a keyword
          ...(dataset.type !== API_DATASET_TYPES_ENUMS.QGISProject && { showLegend: showLegend })
        }
      : createConfigLayerFolder(
          {
            title: datasetTitle,
            group: datasetGroup
          },
          depth
        )

    if (isDataset(dataset)) {
      // QGIS requests need the GetProjectSettings request type injected into the url params
      let url = dataset.url
      if (dataset.type === API_DATASET_TYPES_ENUMS.QGISProject) {
        const paramSeparator = dataset.url.includes('?') ? '&' : '?'
        url = `${dataset.url}${paramSeparator}SERVICE=WMS&REQUEST=GetProjectSettings`
      }

      mapLayer.config = {
        // WFS requests need the GetFeature request type injected into the url params
        ...(dataset.type === API_DATASET_TYPES_ENUMS.WFS && {
          spatial: {
            params: {
              REQUEST: 'GetFeature'
            }
          }
        }),
        // MVT needs to set url inside of spatial param
        ...(dataset.type !== API_DATASET_TYPES_ENUMS.MVT && {
          url
        }),
        ...(dataset.type === API_DATASET_TYPES_ENUMS.MVT && {
          spatial: {
            url
          }
        })
      }
    }

    if (isMapDatasetGroup(dataset)) {
      mapLayer.layers = processDatasetsRecursively(dataset, mapLayer as ConfigLayerFolderOptions)
    }

    mapLayers.push(mapLayer)
  }

  return mapLayers
}

const transformDatasetsConfig = (datasetsConfig: ListDatasetsResponse) => {
  const mapLayers: ConfigLayerOptions[] = []
  for (const mapdataset of datasetsConfig.mapdatasets) {
    const mapLayer = createConfigLayerFolder({
      title: mapdataset.group_name,
      group: mapdataset.group_name + ' Folder'
    })

    const layers = processDatasetsRecursively(mapdataset, mapLayer)
    mapLayer.layers = layers

    mapLayers.push(...layers)
  }

  return {
    layers: mapLayers
  }
}

export const getSiteConfigNew = async (site: string, map: string) => {
  const config = await fetchAllConfig(site, map)

  const { datasets: datasetsConfig, map: mapConfig, site: siteConfig } = config

  const configTransformed = merge({}, transformSiteConfig(siteConfig), transformMapConfig(mapConfig), transformDatasetsConfig(datasetsConfig))

  return configTransformed
}
